/**
 * drupalSettings merge helper - single source of truth.
 *
 * Any SDX code path that needs to patch the live drupalSettings
 * object from an incoming server response goes through this
 * helper. Originally written for the SPA navigation engine but
 * applies identically to AJAX-heavy features like the confirm
 * modal, DataProvider partial reloads, and anywhere else a
 * fresh drupalSettings blob arrives without carrying every
 * session-critical key from the current page.
 *
 * The helper implements an explicit "replace then fill gaps"
 * contract: clear the target, apply the incoming blob, and
 * then restore any navigation-critical keys that the incoming
 * response did not include. Restoration is "fill the gap",
 * never "overwrite" - any key the server sent wins.
 *
 * ## What counts as navigation-critical
 *
 * Two categories are preserved:
 *
 * 1. Hardcoded Drupal-core and SDX-core keys that are set once
 *    per session and must survive every navigation:
 *    - `sdx`              - SDX core settings (activeTheme, route_patterns, ...)
 *    - `sdxEditSession`   - editorial toolbar session state
 *    - `path`             - Drupal's baseUrl, pathPrefix, ...
 *    - `user`             - current user uid, permissionsHash
 *    - `pluralDelimiter`  - Drupal core's plural delimiter constant
 *    - `suppressDeprecationErrors`
 *    - `ajaxTrustedUrl`   - Drupal core's trusted URL whitelist
 *
 * 2. Anything whose key matches the framework-namespace regex
 *    below (sdx_react, sdx_vue, sdx_svelte, sdx_solid, ...).
 *    Auto-detected so new framework modules extend the preserve
 *    set with zero changes to this file - open for extension,
 *    closed for modification.
 *
 * ## Why mutate in place
 *
 * `drupalSettings` is captured as a module-level reference by
 * every Drupal behavior and every mounted component. Replacing
 * the object would leave those captured references pointing at
 * the old data. Mutating in place keeps every consumer in sync
 * with the same object identity.
 */

const CORE_PRESERVE_KEYS: ReadonlySet<string> = new Set([
  'sdx',
  'sdxEditSession',
  'path',
  'user',
  'pluralDelimiter',
  'suppressDeprecationErrors',
  'ajaxTrustedUrl',
]);

const FRAMEWORK_NAMESPACE_PATTERN = /^sdx_[a-z0-9]/;

/**
 * Replaces the contents of `target` with `incoming`, preserving
 * any navigation-critical keys that `incoming` does not provide.
 */
export function mergeDrupalSettings(
  target: Record<string, unknown>,
  incoming: Record<string, unknown>,
): void {
  const preserved: Record<string, unknown> = {};
  for (const key of Object.keys(target)) {
    if (isPreserveKey(key)) {
      preserved[key] = target[key];
    }
  }

  for (const key of Object.keys(target)) {
    delete target[key];
  }
  Object.assign(target, incoming);

  for (const key of Object.keys(preserved)) {
    if (!(key in target)) {
      target[key] = preserved[key];
    }
  }
}

/**
 * Returns true when a key belongs to the navigation-critical
 * preserve set. Exposed for callers that need to reason about
 * preserve semantics without going through the full merge.
 */
export function isPreserveKey(key: string): boolean {
  if (CORE_PRESERVE_KEYS.has(key)) return true;
  return FRAMEWORK_NAMESPACE_PATTERN.test(key);
}
